const CURRENT_VERSION = process.env.REACT_APP_BUILD_VERSION || "dev";

let reloadInProgress = false;

function shouldCheckVersion() {
  return (
    process.env.NODE_ENV === "production" && CURRENT_VERSION !== "dev"
  );
}

function versionUrl() {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/version.json?t=${Date.now()}`;
}

export function getCurrentBuildVersion() {
  return CURRENT_VERSION;
}

export function reloadToLatest() {
  if (reloadInProgress) return;
  reloadInProgress = true;
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Fetches deployed version.json and compares with the running build.
 * @returns {Promise<boolean>} true if a newer version is deployed
 */
export async function isAppOutdated() {
  if (!shouldCheckVersion()) return false;

  try {
    const response = await fetch(versionUrl(), {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!response.ok) return false;

    const data = await response.json();
    const remoteVersion = data && data.version;
    return Boolean(remoteVersion && remoteVersion !== CURRENT_VERSION);
  } catch (err) {
    return false;
  }
}

/**
 * If a newer deploy exists, forces a full reload.
 * @returns {Promise<boolean>} true when a reload was triggered (caller should abort)
 */
export async function reloadIfOutdated() {
  if (reloadInProgress) return true;
  const outdated = await isAppOutdated();
  if (outdated) {
    reloadToLatest();
    return true;
  }
  return false;
}

/**
 * Event-driven checks: once on boot, then on tab focus / visibility.
 * No polling. Returns a cleanup function.
 */
export function bindVersionCheckEvents() {
  if (!shouldCheckVersion()) {
    return () => {};
  }

  const run = () => {
    reloadIfOutdated();
  };

  run();

  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      run();
    }
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", run);

  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", run);
  };
}
